/**
 * Bulk Upload Page
 * ================
 * Upload invoices and quotes with real API integration and OCR processing.
 *
 * Uses WebSocket for real-time OCR status updates (replaces polling).
 *
 * WebSocket Events Listened:
 *   - ocr_status: Updates file status when OCR completes/fails
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import UploadZone from './components/UploadZone';
import FileList from './components/FileList';
import documentApi from '../../services/documentApi';
import { useSocket, OcrStatusEvent } from '../../context/SocketContext';
import './BulkUpload.scss';

export interface UploadedFile {
    id: string;
    name: string;
    size: number;
    type: 'invoice' | 'quote';
    status: 'pending' | 'uploading' | 'processing' | 'done' | 'error';
    serverDocId?: string; // Backend document ID
    error?: string;
    progress?: number; // OCR progress percentage
}

export interface MatchResult {
    id: string;
    invoiceFile: string;
    quoteFile: string;
    status: 'matched' | 'partial' | 'discrepancy' | 'unmatched';
    confidence: number;
    details?: string;
    verification?: any;
}

const BulkUpload: React.FC = () => {
    const navigate = useNavigate();
    const { subscribe, unsubscribe, on, off, isConnected } = useSocket();

    const [invoiceFiles, setInvoiceFiles] = useState<UploadedFile[]>([]);
    const [quoteFiles, setQuoteFiles] = useState<UploadedFile[]>([]);

    // Track files being uploaded
    const pendingFilesRef = useRef<Map<string, File>>(new Map());

    // Map serverDocId -> tempId for WebSocket event routing
    const docIdMapRef = useRef<Map<string, { tempId: string; type: 'invoice' | 'quote' }>>(new Map());

    /**
     * Handle OCR status updates from WebSocket
     */
    const handleOcrStatus = useCallback((event: OcrStatusEvent) => {
        const mapping = docIdMapRef.current.get(event.document_id);
        if (!mapping) return; // Not our document

        const { tempId, type } = mapping;
        const updateFile = type === 'invoice' ? setInvoiceFiles : setQuoteFiles;

        if (event.status === 'completed') {
            updateFile((prev) =>
                prev.map((f) =>
                    f.id === tempId ? { ...f, status: 'done', progress: 100 } : f
                )
            );
            // Cleanup: unsubscribe and remove mapping
            unsubscribe('document', event.document_id);
            docIdMapRef.current.delete(event.document_id);
        } else if (event.status === 'failed') {
            updateFile((prev) =>
                prev.map((f) =>
                    f.id === tempId
                        ? { ...f, status: 'error', error: event.error || 'Processing failed' }
                        : f
                )
            );
            unsubscribe('document', event.document_id);
            docIdMapRef.current.delete(event.document_id);
        } else if (event.status === 'processing') {
            // Update progress
            updateFile((prev) =>
                prev.map((f) =>
                    f.id === tempId ? { ...f, progress: event.progress } : f
                )
            );
        }
    }, [unsubscribe]);

    // Register WebSocket event listener
    useEffect(() => {
        if (!isConnected) {
            return;
        }

        on<OcrStatusEvent>('ocr_status', handleOcrStatus);

        return () => {
            off('ocr_status', handleOcrStatus);
        };
    }, [isConnected, on, off, handleOcrStatus]);

    /**
     * Subscribe to document updates after upload
     */
    const subscribeToDocument = useCallback((serverDocId: string, tempId: string, type: 'invoice' | 'quote') => {
        // Store mapping for event routing
        docIdMapRef.current.set(serverDocId, { tempId, type });

        // Subscribe to document room
        subscribe('document', serverDocId);
    }, [subscribe]);

    // Handle invoice file drops
    const handleInvoiceDrop = useCallback(async (files: File[]) => {
        for (const file of files) {
            const tempId = `inv-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

            const newFile: UploadedFile = {
                id: tempId,
                name: file.name,
                size: file.size,
                type: 'invoice',
                status: 'uploading',
                progress: 0
            };
            setInvoiceFiles((prev) => [...prev, newFile]);
            pendingFilesRef.current.set(tempId, file);

            try {
                const response = await documentApi.uploadDocument(file, 'invoice');

                if (response.success && response.data) {
                    const serverDocId = response.data.id;

                    setInvoiceFiles((prev) =>
                        prev.map((f) =>
                            f.id === tempId
                                ? { ...f, serverDocId, status: 'processing' }
                                : f
                        )
                    );

                    // Subscribe to WebSocket updates instead of polling
                    subscribeToDocument(serverDocId, tempId, 'invoice');
                } else {
                    setInvoiceFiles((prev) =>
                        prev.map((f) =>
                            f.id === tempId
                                ? { ...f, status: 'error', error: response.message }
                                : f
                        )
                    );
                }
            } catch (error) {
                setInvoiceFiles((prev) =>
                    prev.map((f) =>
                        f.id === tempId
                            ? { ...f, status: 'error', error: 'Upload failed' }
                            : f
                    )
                );
            }

            pendingFilesRef.current.delete(tempId);
        }
    }, [subscribeToDocument]);

    // Handle quote file drops
    const handleQuoteDrop = useCallback(async (files: File[]) => {
        for (const file of files) {
            const tempId = `quote-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

            const newFile: UploadedFile = {
                id: tempId,
                name: file.name,
                size: file.size,
                type: 'quote',
                status: 'uploading',
                progress: 0
            };
            setQuoteFiles((prev) => [...prev, newFile]);
            pendingFilesRef.current.set(tempId, file);

            try {
                const response = await documentApi.uploadDocument(file, 'quote');

                if (response.success && response.data) {
                    const serverDocId = response.data.id;

                    setQuoteFiles((prev) =>
                        prev.map((f) =>
                            f.id === tempId
                                ? { ...f, serverDocId, status: 'processing' }
                                : f
                        )
                    );

                    // Subscribe to WebSocket updates instead of polling
                    subscribeToDocument(serverDocId, tempId, 'quote');
                } else {
                    setQuoteFiles((prev) =>
                        prev.map((f) =>
                            f.id === tempId
                                ? { ...f, status: 'error', error: response.message }
                                : f
                        )
                    );
                }
            } catch (error) {
                setQuoteFiles((prev) =>
                    prev.map((f) =>
                        f.id === tempId
                            ? { ...f, status: 'error', error: 'Upload failed' }
                            : f
                    )
                );
            }

            pendingFilesRef.current.delete(tempId);
        }
    }, [subscribeToDocument]);

    // Remove file (also delete from server)
    const handleRemoveFile = async (id: string, type: 'invoice' | 'quote') => {
        const files = type === 'invoice' ? invoiceFiles : quoteFiles;
        const file = files.find((f) => f.id === id);

        // Unsubscribe from WebSocket updates
        if (file?.serverDocId) {
            unsubscribe('document', file.serverDocId);
            docIdMapRef.current.delete(file.serverDocId);

            try {
                await documentApi.deleteDocument(file.serverDocId);
            } catch (error) {
                console.error('Failed to delete from server:', error);
            }
        }

        // Remove from state
        if (type === 'invoice') {
            setInvoiceFiles((prev) => prev.filter((f) => f.id !== id));
        } else {
            setQuoteFiles((prev) => prev.filter((f) => f.id !== id));
        }
    };

    // Clear all files (Reset UI only, do not delete from DB)
    const handleClearAll = async () => {
        // Unsubscribe from all documents
        docIdMapRef.current.forEach((_, docId) => {
            unsubscribe('document', docId);
        });
        docIdMapRef.current.clear();

        // Reset state
        setInvoiceFiles([]);
        setQuoteFiles([]);
    };

    // Check if any files are still processing
    const hasProcessingFiles = [...invoiceFiles, ...quoteFiles].some(
        (f) => f.status === 'uploading' || f.status === 'processing'
    );

    // Navigate to dashboard
    const handleGoToDashboard = () => {
        if (hasProcessingFiles) {
            if (!window.confirm('Some files are still processing. They will continue in the background. Go to Dashboard?')) {
                return;
            }
        }
        navigate('/reconciliation');
    };

    // Warn on page leave if files are processing
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasProcessingFiles) {
                e.preventDefault();
                e.returnValue = 'Files are still processing. Are you sure you want to leave?';
                return e.returnValue;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasProcessingFiles]);

    // Cleanup subscriptions on unmount
    useEffect(() => {
        return () => {
            docIdMapRef.current.forEach((_, docId) => {
                unsubscribe('document', docId);
            });
        };
    }, [unsubscribe]);

    return (
        <Layout
            pageTitle="Bulk Upload"
            pageDescription="Upload invoices and quotes. Matching is done in the Dashboard."
        >
            <div className="bulk-upload">
                {/* Connection status indicator */}
                {!isConnected && (
                    <div className="bulk-upload__connection-warning">
                        Connecting to server...
                    </div>
                )}

                {/* Upload Section */}
                <div className="bulk-upload__grid">
                    {/* Invoices */}
                    <div className="bulk-upload__section">
                        <h3 className="bulk-upload__section-title">
                            Invoices
                        </h3>
                        <UploadZone
                            accept=".pdf,.png,.jpg,.jpeg"
                            onDrop={handleInvoiceDrop}
                            label="Drop invoices here"
                        />
                        <FileList
                            files={invoiceFiles}
                            onRemove={(id) => handleRemoveFile(id, 'invoice')}
                        />
                    </div>

                    {/* Quotes */}
                    <div className="bulk-upload__section">
                        <h3 className="bulk-upload__section-title">
                            Quotes
                        </h3>
                        <UploadZone
                            accept=".pdf,.png,.jpg,.jpeg"
                            onDrop={handleQuoteDrop}
                            label="Drop quotes here"
                        />
                        <FileList
                            files={quoteFiles}
                            onRemove={(id) => handleRemoveFile(id, 'quote')}
                        />
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="bulk-upload__actions">
                    <button
                        className="bulk-upload__btn bulk-upload__btn--secondary"
                        onClick={handleClearAll}
                        disabled={invoiceFiles.length === 0 && quoteFiles.length === 0}
                    >
                        Clear All
                    </button>
                    <button
                        className="bulk-upload__btn bulk-upload__btn--primary"
                        onClick={handleGoToDashboard}
                        disabled={invoiceFiles.length === 0 && quoteFiles.length === 0}
                        style={{ backgroundColor: '#10b981', borderColor: '#10b981' }}
                    >
                        {hasProcessingFiles
                            ? 'Processing... Go to Invoices'
                            : 'Go to Invoices'}
                    </button>
                </div>

                {/* Help Text */}
                <div className="bulk-upload__help">
                    <p>
                        <strong>Supported formats:</strong> PDF, PNG, JPG (max 10MB)
                    </p>
                    <p>
                        Upload your invoices in the left column and corresponding quotes in the right column.<br />
                        Files are automatically processed in the background. Use the <strong>Match & Analyze</strong> button on the Invoices Dashboard to link them.
                    </p>
                </div>
            </div>
        </Layout>
    );
};

export default BulkUpload;
